import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Table2 } from 'lucide-react';
import type { CubeQuerySchema } from 'shared/src/api/cube-query-schema';
import type { LabeledVizSpec } from 'shared/src/api/ai-labeled-viz-types';
import type { NoDataViz } from 'shared/src/api/ai-viz-types';
import type { CubeMemberInfo, EntityLinks } from 'shared/src/trpc-types/cube-load-trpc-types';
import { deriveRawColumns } from 'shared/src/utils/ai/ai-viz-transformers';
import { trpc } from '@/utils/trpc-client';
import { RawDataTable } from '@/components/ai-visualisations/data-table';
import { CubeQueryCard } from './cube-query-card';
import { VizTypeBadge } from './viz-type-badge';
import { ChartRenderer } from './chart-renderer';
import { collapseMotion } from './motion-presets';

export type UseVisualizationDataResult = {
  rows: Record<string, unknown>[];
  memberMeta: Record<string, CubeMemberInfo>;
  links?: EntityLinks;
  isLoading: boolean;
  isError: boolean;
};

type VizStatus = 'executing-query' | 'selecting-viz' | 'complete';

type VisualizationData = {
  query: CubeQuerySchema;
  labeledViz: LabeledVizSpec | NoDataViz;
  totalRows?: number;
};

const EMPTY_ROWS: Record<string, unknown>[] = [];

const EMPTY_META: Record<string, CubeMemberInfo> = {};

const fadeIn = {
  initial: { opacity: 0, y: 4 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.3 }
};
const STATUS_LABELS: Record<string, string> = {
  'executing-query': 'Executing query...',
  'selecting-viz': 'Selecting visualization...'
};

export type VisualizationBlockProps = {
  cubeQuery?: CubeQuerySchema;
  vizStatus?: VizStatus;
  visualization?: VisualizationData;
  vizUrl?: string;
  hideCopyButtons?: boolean;
};

export function VisualizationBlock(props: VisualizationBlockProps) {
  const data = useVisualizationData(props.visualization?.query);
  return <VisualizationBlockWithData {...props} data={data} />;
}

type BlockWithDataProps = VisualizationBlockProps & {
  data?: UseVisualizationDataResult;
  vizUrl?: string;
};

export function VisualizationBlockWithData({
  cubeQuery,
  vizStatus,
  visualization,
  data,
  vizUrl,
  hideCopyButtons
}: BlockWithDataProps) {
  const isQueryLoading = !data || data.isLoading;
  const chartReady = !!visualization && !!data && !data.isLoading;
  const [showRawData, setShowRawData] = React.useState(false);
  const [userOverride, setUserOverride] = React.useState<'expanded' | 'compact' | null>(null);
  const mode = userOverride ?? (chartReady ? 'compact' : 'expanded');

  const rawColumns = React.useMemo(
    () => deriveRawColumns(data?.rows ?? EMPTY_ROWS, data?.memberMeta),
    [data?.rows, data?.memberMeta]
  );

  const handleToggle = React.useCallback(() => {
    setUserOverride(prev => (prev === 'compact' || prev === null ? 'expanded' : 'compact'));
  }, [setUserOverride]);

  const showToggle =
    chartReady &&
    visualization &&
    visualization.labeledViz.vizType !== 'table' &&
    visualization.labeledViz.vizType !== 'no_data';

  return (
    <div className="space-y-1">
      <div className="overflow-hidden rounded-lg border border-border/50 bg-muted/30">
        {cubeQuery ? (
          <motion.div {...fadeIn}>
            <CubeQueryCard
              query={cubeQuery}
              memberMeta={data?.memberMeta}
              isLoading={isQueryLoading}
              mode={mode}
              onToggle={chartReady ? handleToggle : undefined}
              vizUrl={chartReady ? vizUrl : undefined}
              labeledViz={chartReady ? visualization?.labeledViz : undefined}
              hideCopyButtons={hideCopyButtons}
            />
          </motion.div>
        ) : null}

        {vizStatus && vizStatus !== 'complete' ? (
          <motion.div
            {...fadeIn}
            className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
            <span>{STATUS_LABELS[vizStatus] ?? vizStatus}</span>
          </motion.div>
        ) : null}

        {visualization && !chartReady ? (
          <motion.div {...fadeIn} className="flex items-center gap-2 px-3 py-2">
            <VizTypeBadge vizType={visualization.labeledViz.vizType} />
          </motion.div>
        ) : null}

        {chartReady && visualization ? (
          <motion.div {...fadeIn} className="border-t border-border/50 p-3">
            {data.isError ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                Failed to load chart data
              </div>
            ) : (
              <div className="relative">
                <ChartRenderer viz={visualization.labeledViz} rows={data.rows} links={data.links} />
                {showToggle ? (
                  <button
                    type="button"
                    onClick={() => setShowRawData(prev => !prev)}
                    className="absolute bottom-4 right-4 flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                    <Table2 className="h-3.5 w-3.5" />
                    {showRawData ? 'Hide data' : 'Show data'}
                  </button>
                ) : null}
              </div>
            )}
          </motion.div>
        ) : null}
      </div>

      {showToggle ? (
        <AnimatePresence initial={false}>
          {showRawData ? (
            <motion.div key="raw-data" {...collapseMotion}>
              <RawDataTable rows={data!.rows} columns={rawColumns} links={data!.links} />
            </motion.div>
          ) : null}
        </AnimatePresence>
      ) : null}
    </div>
  );
}

function useVisualizationData(query?: CubeQuerySchema): UseVisualizationDataResult | undefined {
  const { data, isLoading, isError } = trpc.cubeLoad.useQuery(query!, { enabled: !!query });

  if (!query) return undefined;

  return {
    rows: data?.rows ?? EMPTY_ROWS,
    memberMeta: data?.memberMeta ?? EMPTY_META,
    links: data?.links,
    isLoading,
    isError
  };
}
